import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection, isDevMode, APP_INITIALIZER } from "@angular/core";
import { HashLocationStrategy, LocationStrategy } from "@angular/common";
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi, withJsonpSupport, withXhr } from "@angular/common/http";
import { provideAnimations } from "@angular/platform-browser/animations";
import { NoPreloading, provideRouter, withRouterConfig } from "@angular/router";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { NgSelectModule } from "@ng-select/ng-select";
import { NgOptionHighlightDirective } from "@ng-select/ng-option-highlight";
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { ToastrModule } from "ngx-toastr";
import { ModalModule } from "ngx-bootstrap/modal";
import { TimepickerConfig } from "ngx-bootstrap/timepicker";
import { TypeaheadModule } from "ngx-bootstrap/typeahead";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { BsDatepickerModule } from "ngx-bootstrap/datepicker";
import { firstValueFrom } from "rxjs";
import { ColorService } from "./services/common/color.service";
import { AppUpdateService } from "./services/common/app-update.service";
import { AuthenticationService } from "./services/authentication/authentication.service";
import { ROUTES } from "./app.routes";
import { ICheckModule } from "./modules/icheck/icheck.module";
import { NotificationsModule } from "./modules/notification";
import { WizardModule } from "./modules/wizard/wizard.module";
import { LoggingErrorHandlerOptions, LoggingErrorHandlerProviders } from "./services/logging/logging-error-handler";
import { HttpRequestInterceptor } from "./services/authentication/authentication.interceptor";
import { CustomMissingTranslationHandler } from "./common/missingTransLationHandler";
import { NgxLoadingModule } from "./modules/loading/ngx-loading.module";
import { ngxLoadingAnimationTypes } from "./modules/loading/ngx-loading-config";
import { provideServiceWorker } from "@angular/service-worker";
import { provideAppHighcharts } from "./highcharts.providers";

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

export function getTimepickerConfig(): TimepickerConfig {
  const timeStr = new Intl.DateTimeFormat(navigator.language, { hour: "numeric" }).format(new Date());
  return Object.assign(new TimepickerConfig(), { showMeridian: /am|pm/i.test(timeStr) });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    provideHttpClient(withXhr(), withInterceptorsFromDi(), withJsonpSupport()),
    provideRouter(
      ROUTES,
      withRouterConfig({
        onSameUrlNavigation: "reload",
        // Detail pages are nested as `<Entity>Details/:id/<SubPage>`, which puts `:id` on the
        // *parent* route. Every sub-page component reads `params["id"]` from its own
        // ActivatedRoute, so parent params must be inherited or every one of them breaks.
        paramsInheritanceStrategy: "always",
      }),
    ),
    importProvidersFrom(
      FormsModule,
      ReactiveFormsModule,
      NgSelectModule,
      NgOptionHighlightDirective,
      ModalModule,
      TypeaheadModule,
      BsDropdownModule,
      BsDatepickerModule,
      NgxLoadingModule.forRoot({
        animationType: ngxLoadingAnimationTypes.rectangleBounce,
        backdropBackgroundColour: "rgba(255,255,255,0.7)",
        backdropBorderRadius: "4px",
        primaryColour: "#DC8139",
        secondaryColour: "#DC8139",
        tertiaryColour: "#DC8139",
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
        missingTranslationHandler: { provide: MissingTranslationHandler, useClass: CustomMissingTranslationHandler },
      }),
      NotificationsModule,
      ToastrModule.forRoot({
        timeOut: 10000,
        positionClass: "toast-bottom-right",
        preventDuplicates: true,
      }),
      WizardModule,
      ICheckModule.forRoot(),
    ),
    LoggingErrorHandlerProviders,
    {
      provide: LoggingErrorHandlerOptions,
      useValue: {
        rethrowError: false,
        unwrapError: false,
      },
    },
    { provide: TimepickerConfig, useFactory: getTimepickerConfig },
    provideAppHighcharts(),
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: (colorService: ColorService, authService: AuthenticationService) => () => {
        const whitelabel = authService.getWhitelabel();
        if (!whitelabel) return Promise.resolve();
        return firstValueFrom(colorService.getThemeColors(whitelabel)).then((colors) => colorService.addTheme(colors));
      },
      deps: [ColorService, AuthenticationService],
      multi: true,
    },
    {
      // Watches for a newer deployed build and recovers when this build's chunks are gone.
      provide: APP_INITIALIZER,
      useFactory: (appUpdateService: AppUpdateService) => () => appUpdateService.start(),
      deps: [AppUpdateService],
      multi: true,
    },
    provideServiceWorker("ngsw-worker.js", {
      // The PWA/service worker is only shipped in the Trips build (see the "trips" configuration in angular.json).
      // Gate registration on the TripsOnly flag so the main FleetManagement app never registers it.
      enabled: !isDevMode() && !!(globalThis as { server_variables?: { TripsOnly?: boolean } }).server_variables?.TripsOnly,
      registrationStrategy: "registerWhenStable:30000",
    }),
  ],
};
